import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { Grid, Typography, Button, TextField, useMediaQuery, useTheme } from "@mui/material";
import ContentWrapper from "../Layout/ContentWrapper";
import { getLeaderboardAction } from "../../store/reducers/results";
import { useContestId } from "../../hooks/useContestId";

// Prefer the resolved unique_name from mainapp-prd; fall back to the short
// placeholder handle so we're never blank while names load or for legacy rows.
const displayHandle = (row) => {
  if (row?.unique_name) return row.unique_name;
  const id = row?.spotlight_user_id;
  if (!id) return "Player";
  return `Player ${String(id).slice(0, 4).toUpperCase()}`;
};

// First two alphanumerics of the handle, uppercased — "dk613" -> "DK",
// "bumblebee" -> "BU".
const initials = (name) => {
  const cleaned = String(name || "").replace(/[^a-zA-Z0-9]/g, "");
  return cleaned.slice(0, 2).toUpperCase() || "?";
};

// Stable per-user avatar color (hashed off the id so it never changes between
// loads). Mirrors the multi-color avatars in the Spotlight reading leaderboard.
const AVATAR_COLORS = [
  "#2F6BFF", "#7C3AED", "#10B981", "#F59E0B",
  "#EC4899", "#06B6D4", "#6366F1", "#EF4444",
];
const avatarColor = (key) => {
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const Leaderboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const contestId = useContestId();

  const userId = useSelector((state) => state.app?.userId);

  const leaderboard = useSelector((state) => state.results?.leaderboard);
  const meta = useSelector((state) => state.results?.leaderboardMeta);
  const currentUser = useSelector((state) => state.results?.currentUser);
  const loading = useSelector((state) => state.results?.leaderboardLoading);

  // The server ranks the entire field; we fetch one 100-row window at a time.
  const PAGE = 100;
  const [offset, setOffset] = useState(0);
  const [jumpValue, setJumpValue] = useState("");

  // Switching contest resets to the first page.
  useEffect(() => {
    setOffset(0);
  }, [contestId]);

  useEffect(() => {
    if (!userId) return;
    dispatch(getLeaderboardAction({ userId, contestId, limit: PAGE, offset }));
  }, [dispatch, userId, contestId, offset]);

  // Pin the heading beneath the frozen Nav so the title + entrant count stay
  // visible while scrolling the standings. Nav height is dynamic, so measure it.
  const [navOffset, setNavOffset] = useState(0);
  useEffect(() => {
    const nav = document.getElementById("app-nav");
    if (!nav) return;
    const update = () => setNavOffset(nav.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  const sorted = _.sortBy(leaderboard || [], "rank");

  // Drive paging off the offset the DISPLAYED data reflects (meta.offset), not
  // the pending offset, so list + podium stay consistent while the next page
  // loads. The podium (top 3) only makes sense on the first page.
  const total = meta?.totalEntrants || 0;
  const dataOffset = meta?.offset ?? 0;
  const onFirstPage = dataOffset === 0;
  const currentPage = Math.floor(dataOffset / PAGE) + 1;
  const totalPages = total > 0 ? Math.ceil(total / PAGE) : 1;
  const hasPrev = dataOffset > 0;
  const hasNext = dataOffset + PAGE < total;

  const top3 = onFirstPage ? sorted.slice(0, 3) : [];
  const rest = onFirstPage ? sorted.slice(3) : sorted;

  const myRank = currentUser?.rank;
  const myPageOffset = myRank ? Math.floor((myRank - 1) / PAGE) * PAGE : null;
  const canJumpToMe = myPageOffset != null && myPageOffset !== dataOffset;

  // Back to the first page (rank #1).
  const goToTop = () => setOffset(0);

  // Jump straight to the page that holds a typed rank (clamped to the field).
  const goToRank = () => {
    const n = parseInt(jumpValue, 10);
    if (!Number.isFinite(n) || n < 1) return;
    const clamped = total ? Math.min(n, total) : n;
    setOffset(Math.floor((clamped - 1) / PAGE) * PAGE);
  };

  const isCurrentUser = (row) =>
    !!currentUser && row?.user_id === currentUser?.user_id;

  const cardSx = [
    {
      width: "100%",
      borderRadius: "16px",
      border: `1px solid ${theme.branding.gray[200]}`,
      backgroundColor: theme.branding.gray[0],
    },
    (theme) =>
      theme.applyStyles("dark", {
        borderColor: theme.branding.darkBlue[600],
        backgroundColor: theme.branding.darkBlue[800],
      }),
  ];

  // ---------- circular avatar with initials ----------
  const Avatar = ({ name, idKey, size }) => (
    <Grid
      container
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        borderRadius: "50%",
        backgroundColor: avatarColor(idKey),
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 10px -4px rgba(0,0,0,0.35)",
      }}
    >
      <Typography
        sx={{
          color: "#fff",
          fontWeight: 700,
          fontSize: `${Math.round(size * 0.36)}px`,
          lineHeight: 1,
        }}
      >
        {initials(name)}
      </Typography>
    </Grid>
  );

  // ---------- tiebreaker inline display ----------
  // David's format: "42 pts • TB 3" — bold dot separator, all inline.
  // Returns null when score is -1 (not yet scored) so nothing renders.
  const hasTiebreaker = (score) => score != null && score !== -1;

  const TiebreakerInline = ({ score, fontSize = "13px" }) => {
    const pending = !hasTiebreaker(score);
    return (
      <Typography
        component="span"
        sx={{ color: theme.branding.gray[400], fontSize, fontWeight: 400 }}
      >
        {" "}
        <Typography
          component="span"
          sx={{ color: theme.branding.gray[500], fontWeight: 700, fontSize }}
        >
          •
        </Typography>
        {pending ? (
          <Typography
            component="span"
            sx={{ color: theme.branding.gray[400], fontStyle: "italic", fontSize }}
          >
            {" TB pending"}
          </Typography>
        ) : (
          <>
            {" TB "}
            <Typography
              component="span"
              sx={{ color: theme.branding.gray[600], fontWeight: 700, fontSize }}
            >
              {score}
            </Typography>
          </>
        )}
      </Typography>
    );
  };

  // ---------- podium spot (top 3) ----------
  const PodiumSpot = ({ row, place }) => {
    // Keep the column in the layout even when empty (fewer than 3 entrants), so
    // #1 stays centered.
    if (!row) return <Grid sx={{ flex: 1 }} />;
    const size = place === 1 ? 92 : 70;
    const medal =
      place === 1 ? "#F5A623" : place === 2 ? theme.branding.gray[400] : "#CD7F32";
    const handle = displayHandle(row);
    const me = isCurrentUser(row);

    return (
      <Grid
        container
        direction="column"
        sx={{
          flex: 1,
          minWidth: 0,
          alignItems: "center",
          gap: "10px",
          marginTop: place === 1 ? "0px" : "28px",
        }}
      >
        <Grid sx={{ position: "relative", width: `${size}px`, height: `${size}px` }}>
          <Avatar name={handle} idKey={row.spotlight_user_id} size={size} />
          {/* rank badge tucked under the avatar */}
          <Grid
            container
            sx={{
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "auto",
              padding: "1px 9px",
              borderRadius: "10px",
              backgroundColor: medal,
              border: `2px solid ${theme.branding.gray[0]}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.4 }}
            >
              #{place}
            </Typography>
          </Grid>
        </Grid>

        <Grid container direction="column" sx={{ alignItems: "center", gap: "4px", minWidth: 0, marginTop: "4px" }}>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ fontWeight: 600, maxWidth: "100%", color: me ? theme.branding.cyan[600] : "inherit" }}
          >
            {handle}
          </Typography>
          <Typography sx={{ color: theme.branding.cyan[500], fontWeight: 700, fontSize: place === 1 ? "20px" : "16px" }}>
            {row.points_earned}
            <Typography component="span" variant="caption" sx={{ color: theme.branding.gray[400], fontWeight: 400, fontSize: place === 1 ? "13px" : "11px" }}> pts</Typography>
            <TiebreakerInline score={row.tiebreaker_score} fontSize={place === 1 ? "13px" : "11px"} />
          </Typography>
        </Grid>
      </Grid>
    );
  };

  // ---------- ranked list row (#4+) ----------
  const ListRow = ({ row }) => {
    const handle = displayHandle(row);
    const me = isCurrentUser(row);
    return (
      <Grid
        container
        direction="row"
        wrap="nowrap"
        sx={[
          {
            width: "100%",
            padding: "12px 16px",
            borderRadius: "12px",
            alignItems: "center",
            gap: "12px",
            border: `1px solid ${me ? theme.branding.cyan[300] : "transparent"}`,
            backgroundColor: me ? "#33DDEE0D" : "transparent",
          },
          (theme) =>
            theme.applyStyles("dark", {
              borderColor: me ? theme.branding.cyan[600] : "transparent",
              backgroundColor: me ? "#33DDEE14" : "transparent",
            }),
        ]}
      >
        <Typography
          sx={{ width: "34px", minWidth: "34px", color: theme.branding.gray[500], fontWeight: 600 }}
        >
          #{row.rank}
        </Typography>
        <Avatar name={handle} idKey={row.spotlight_user_id} size={38} />
        <Grid container direction="column" sx={{ flex: 1, minWidth: 0 }}>
          <Grid container direction="row" wrap="nowrap" sx={{ alignItems: "center", gap: "8px", minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
              {handle}
            </Typography>
            {me && (
              <Grid container sx={{ width: "auto", padding: "1px 8px", borderRadius: "6px", backgroundColor: theme.branding.cyan[500] }}>
                <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700 }}>
                  YOU
                </Typography>
              </Grid>
            )}
          </Grid>
          <Typography variant="caption" sx={{ color: theme.branding.gray[400] }}>
              {row.picks_correct} correct
              <TiebreakerInline score={row.tiebreaker_score} fontSize="12px" />
            </Typography>
        </Grid>
        <Typography sx={{ width: "auto", color: theme.branding.cyan[600], fontWeight: 700, whiteSpace: "nowrap" }}>
          {row.points_earned} pts
        </Typography>
      </Grid>
    );
  };

  return (
    <ContentWrapper>
      <Grid container direction="column" sx={{ width: "100%", gap: "20px" }}>
        {/* header — sticky beneath the frozen Nav */}
        <Grid
          container
          direction={isMobile ? "column" : "row"}
          wrap="nowrap"
          sx={[
            {
              position: "sticky",
              top: `${navOffset}px`,
              zIndex: 60,
              backgroundColor: theme.branding.gray[0],
              gap: "8px",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              padding: "16px 0",
              borderBottom: `1px solid ${theme.branding.gray[200]}`,
            },
            (theme) =>
              theme.applyStyles("dark", {
                backgroundColor: theme.branding.darkBlue[800],
              }),
          ]}
        >
          <Grid container direction="column" sx={{ gap: "8px" }}>
            <Typography variant="h2">Leaderboard</Typography>
            <Typography variant="subtitle1" sx={{ color: theme.branding.gray[500] }}>
              World Cup 2026 ·{" "}
              {meta?.totalEntrants ? `${meta.totalEntrants} entrants` : "standings"}
            </Typography>
          </Grid>
        </Grid>

        {!loading && _.isEmpty(sorted) ? (
          <Grid
            container
            sx={[
              {
                borderRadius: "16px",
                width: "100%",
                padding: isMobile ? "48px 16px" : "96px 16px",
                backgroundColor: theme.branding.gray[0],
                border: `1px solid ${theme.branding.gray[200]}`,
                justifyContent: "center",
                alignItems: "center",
              },
              (theme) =>
                theme.applyStyles("dark", {
                  borderColor: theme.branding.darkBlue[600],
                  backgroundColor: theme.branding.darkBlue[800],
                }),
            ]}
          >
            <Typography variant="subtitle1" sx={{ color: theme.branding.gray[400] }}>
              No entrants have started scoring yet.
            </Typography>
          </Grid>
        ) : (
          <>
            {/* pager controls — top (Prev / Next / Top / jump-to-rank) */}
            {total > PAGE && (
              <Grid
                container
                direction={isMobile ? "column" : "row"}
                wrap="wrap"
                sx={{ alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: "10px" }}
              >
                <Grid container direction="row" wrap="nowrap" sx={{ width: "auto", alignItems: "center", gap: "8px" }}>
                  <Button variant="outlined" size="small" disabled={!hasPrev || loading}
                    onClick={() => setOffset(Math.max(0, dataOffset - PAGE))} sx={{ textTransform: "none" }}>‹ Prev</Button>
                  <Button variant="outlined" size="small" disabled={!hasNext || loading}
                    onClick={() => setOffset(dataOffset + PAGE)} sx={{ textTransform: "none" }}>Next ›</Button>
                  <Button variant="text" size="small" disabled={dataOffset === 0 || loading}
                    onClick={goToTop} sx={{ textTransform: "none", color: theme.branding.cyan[600] }}>↑ Top</Button>
                </Grid>
                <Typography variant="caption" sx={{ color: theme.branding.gray[500], whiteSpace: "nowrap" }}>
                  Page {currentPage} of {totalPages}
                  {"  ·  "}
                  {total === 0 ? 0 : dataOffset + 1}–{Math.min(dataOffset + PAGE, total)} of {total}
                </Typography>
                <Grid container direction="row" wrap="nowrap" sx={{ width: "auto", alignItems: "center", gap: "8px" }}>
                  <TextField
                    size="small"
                    type="number"
                    value={jumpValue}
                    onChange={(e) => setJumpValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") goToRank(); }}
                    placeholder="Rank #"
                    inputProps={{ min: 1, max: total || undefined, "aria-label": "Jump to rank" }}
                    sx={{ width: "110px" }}
                  />
                  <Button variant="contained" size="small" disabled={loading || !jumpValue}
                    onClick={goToRank} sx={{ textTransform: "none" }}>Go</Button>
                </Grid>
              </Grid>
            )}

            {/* your-rank card */}
            {currentUser && (
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={[
                  ...cardSx,
                  { padding: "18px 22px", justifyContent: "space-between", alignItems: "center" },
                ]}
              >
                <Grid container direction="column" sx={{ width: "auto", gap: "4px" }}>
                  <Typography variant="caption" sx={{ color: theme.branding.gray[500] }}>
                    Your rank
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {currentUser.points_earned} pts · {currentUser.picks_correct} correct
                    <TiebreakerInline score={currentUser.tiebreaker_score} fontSize="14px" />
                  </Typography>
                </Grid>
                <Grid container direction="column" sx={{ width: "auto", alignItems: "flex-end", gap: "2px" }}>
                  <Typography variant="h2" sx={{ color: theme.branding.cyan[500] }}>
                    #{currentUser.rank}
                  </Typography>
                  {canJumpToMe && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setOffset(myPageOffset)}
                      sx={{ textTransform: "none", color: theme.branding.cyan[600], padding: 0, minWidth: 0 }}
                    >
                      Go to my position
                    </Button>
                  )}
                </Grid>
              </Grid>
            )}

            {/* podium — top 3 (#1 raised in the middle) */}
            {top3.length > 0 && (
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={[
                  ...cardSx,
                  {
                    padding: "24px 12px 28px",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: "4px",
                  },
                ]}
              >
                <PodiumSpot row={top3[1]} place={2} />
                <PodiumSpot row={top3[0]} place={1} />
                <PodiumSpot row={top3[2]} place={3} />
              </Grid>
            )}

            {/* the rest of the field */}
            {rest.length > 0 && (
              <Grid container direction="column" sx={[...cardSx, { padding: "8px", gap: "2px" }]}>
                {rest.map((row) => (
                  <ListRow key={`lb-${row.user_id}-${row.rank}`} row={row} />
                ))}
              </Grid>
            )}

            {/* pager controls — bottom (after the 100 rows) */}
            {total > PAGE && (
              <Grid
                container
                direction="row"
                wrap="wrap"
                sx={{ alignItems: "center", justifyContent: "space-between", padding: "4px 4px 24px", gap: "10px" }}
              >
                <Grid container direction="row" wrap="nowrap" sx={{ width: "auto", alignItems: "center", gap: "8px" }}>
                  <Button variant="outlined" size="small" disabled={!hasPrev || loading}
                    onClick={() => setOffset(Math.max(0, dataOffset - PAGE))} sx={{ textTransform: "none" }}>‹ Prev</Button>
                  <Button variant="outlined" size="small" disabled={!hasNext || loading}
                    onClick={() => setOffset(dataOffset + PAGE)} sx={{ textTransform: "none" }}>Next ›</Button>
                </Grid>
                <Typography variant="caption" sx={{ color: theme.branding.gray[500], textAlign: "center", whiteSpace: "nowrap" }}>
                  Page {currentPage} of {totalPages}
                  {"  ·  "}
                  {total === 0 ? 0 : dataOffset + 1}–{Math.min(dataOffset + PAGE, total)} of {total}
                </Typography>
                <Button variant="text" size="small" disabled={dataOffset === 0 || loading}
                  onClick={goToTop} sx={{ textTransform: "none", color: theme.branding.cyan[600] }}>↑ Top</Button>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </ContentWrapper>
  );
};

export default Leaderboard;
